// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDebounce, useEvent, useLatest, useUpdateEffect, useEffectOnce } from "react-use"
import { equals } from "ramda"
import SearchDropdownList from "./SearchDropdownList"
import SearchDropdownDraggableList from "./SearchDropdownDraggableList"
import {
  Column,
  DropdownContent,
  Input,
  InputWrapper,
  ListHeader,
  ListHeaderPrimary,
  ListsWrapper,
  RecentListHeader,
  SearchResults,
  StyledFavouritedIcon,
} from "./styles"
import {
  Destination,
  ListItemEitherType,
  ListItemFavouriteType,
  ListItemType,
} from "./types"
import { useDropdownContentHandling } from "./useDropdownContentHandling"
import styled from "styled-components/macro"
import SvgIcon from "../SvgIcon"
import Spinner from "../Spinner"
import { t } from "@lingui/macro"
import { Trans } from "@lingui/react"
import { ViewMode } from "../../tags/slice"
import { DROPDOWN_CONTENT_CLASSNAME } from "../classNames"
import { isTouchDevice, lineBreak } from "../../../lib/utils"

const StyledSearchThinIcon = styled(SvgIcon)<{ isActive: boolean }>`
  width: 20px;
  min-width: 20px;
  height: 20px;
  color: ${({ theme, isActive }): string => isActive ? theme.colors.indigo : theme.colors.grey};
  margin: 10px 10px 0 0;
  cursor: pointer;
`

const StyledSpinner = styled(Spinner)`
  position: absolute;
  top: 21px;
  right: 52px;
`

const BrowseModeIcon = styled.span<{ isActive: boolean }>`
  display: inline-block;
  vertical-align: top;
  width: 18px;
  height: 20px;
  position: relative;
  margin: 12px 10px 0 0;
  cursor: pointer;
  
  &:after {
    content: "";
    display: inline-block;
    width: 18px;
    height: 3px;
    position: absolute;
    top: 2px;
    left: 0;
    box-shadow: 0 6px ${({ theme, isActive }): string => isActive ? theme.colors.indigo : theme.colors.dustyGray},
                0 12px ${({ theme, isActive }): string => isActive ? theme.colors.indigo : theme.colors.dustyGray};
  }
  
  &:before {
    content: "";
    display: inline-block;
    width: 15px;
    height: 3px;
    position: absolute;
    top: 2px;
    left: 0;
    background-color: ${({ theme, isActive }): string => isActive ? theme.colors.indigo : theme.colors.dustyGray};
  }
`

const initialActiveList: ListItemEitherType[] = []

type Props = {
  style?: React.CSSProperties,
  headerRef: HTMLButtonElement | null,
  setIsDropdownVisible: (isVisible: boolean) => void,
  timeGroupsIds: Destination,
  timeRowsIds: Destination,
  addedItemIds: string[],
  searchSortDropdown: React.ReactElement,
  recentSortDropdown: React.ReactElement,
  placeholder: string,
  favouritesList: ListItemFavouriteType[],
  recentList: ListItemType[],
  searchResultList: ListItemType[],
  viewMode: ViewMode,
  isSearching: boolean,
  isFocusable: boolean,
  orderBy: number,
  showFavouriteButton: boolean,
  onSearchItems: (searchText: string) => void,
  onResetSearchItems: () => void,
  onAddItemToDestination: (refId: string, timeGroupsIds: Destination, timeRowsIds: Destination) => void,
  // event param will be used in parent component callbacks for calling event.stopPropagation()
  // to avoid conflict with wrapper click(onAddItemToDestination)
  onFavouriteItem: (event: React.MouseEvent<HTMLButtonElement>, refId: string) => void,
  onUnfavouriteItem: (event: React.MouseEvent<HTMLButtonElement>, id: string) => void,
  onChangeViewMode: (value: ViewMode) => void,
  onReorderDraggableItem: (id: string, newIndex: number) => void,
}

const SearchDropdownContent = forwardRef<HTMLDivElement, Props>(({
  style = {},
  headerRef,
  setIsDropdownVisible,
  timeGroupsIds,
  timeRowsIds,
  addedItemIds,
  searchSortDropdown,
  recentSortDropdown,
  placeholder,
  favouritesList,
  recentList,
  searchResultList,
  viewMode,
  isSearching,
  orderBy,
  isFocusable,
  onSearchItems,
  onResetSearchItems,
  onAddItemToDestination,
  onFavouriteItem,
  onUnfavouriteItem,
  onChangeViewMode,
  onReorderDraggableItem,
  showFavouriteButton,
}: Props, ref): React.ReactElement<Props> => {
  const inputRef = useRef<HTMLInputElement | null>(null)

  const {
    activeIdIndex,
    setActiveIdIndex,
    activeId,
    setActiveId,
    handleArrowNavigation,
  } = useDropdownContentHandling()

  const [isSearchResultVisible, setIsSearchResultVisible] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [activeList, setActiveList] = useState<ListItemEitherType[]>(initialActiveList)
  const latestActiveList = useLatest(activeList)
  const isBrowseModeEnabled = viewMode === ViewMode.BROWSE

  const resetArrowNavigation = (): void => {
    if (isSearchResultVisible || isBrowseModeEnabled) {
      setActiveList(searchResultList)
    } else {
      setActiveList(initialActiveList)
    }
    setActiveIdIndex(undefined)
    setActiveId(undefined)
  }

  const onMouseMove = (): void => {
    if (activeIdIndex === undefined && activeId === undefined) {
      return
    }
    resetArrowNavigation()
  }

  const handleAddItemToDestination = (id: string): void => {
    if (isSearching) {
      return
    }

    isFocusable ? headerRef?.focus() : setIsDropdownVisible(false)
    onAddItemToDestination(id, timeGroupsIds, timeRowsIds)
  }

  const SEARCH_ALL_ITEMS = ""

  useEffectOnce((): void => {
    if (viewMode === ViewMode.BROWSE) {
      // Using a blank string as search term will list all items
      onSearchItems(SEARCH_ALL_ITEMS)
    }
  })

  useUpdateEffect((): void => {
    // single space value is a wildcard to show all tags sorted alphabetically
    if (inputValue.trim().length > 0) {
      setIsSearchResultVisible(true)
      onChangeViewMode(ViewMode.SEARCH)
    } else {
      setIsSearchResultVisible(false)
      onResetSearchItems()
    }
  }, [inputValue])

  useDebounce((): void => {
    const trimmedInputValue = inputValue.trim()
    if (trimmedInputValue.length > 0) {
      onSearchItems(trimmedInputValue)
    }
  }, 300, [inputValue, orderBy])

  useEffect((): void => {
    if (isTouchDevice) {
      return
    }
    inputRef.current?.focus()
  }, [])

  useEffect(resetArrowNavigation, [searchResultList])

  const getActiveList = useMemo((): ListItemEitherType[] => {
    if (isSearchResultVisible || isBrowseModeEnabled) {
      return searchResultList
    }
    if (favouritesList.length !== 0) {
      return favouritesList
    }
    if (recentList.length !== 0) {
      return recentList
    }
    return initialActiveList
  }, [
    isSearchResultVisible,
    isBrowseModeEnabled,
    favouritesList.length,
    recentList.length,
  ])

  const onKeyDown = useCallback((e: React.KeyboardEvent): void => {
    if (e.shiftKey && e.key === "Tab" && isFocusable && headerRef) {
      headerRef.tabIndex = -1
      headerRef.focus()
      setIsDropdownVisible(false)
      setTimeout(() => {
        headerRef.tabIndex = 0
      })
      return
    }
    if (e.key === "Tab") {
      isFocusable ? headerRef?.focus() : e.preventDefault()
    }
    if (e.key === "Escape") {
      setIsDropdownVisible(false)
    }
    if (e.key === " " && inputValue.length === 0) {
      e.preventDefault()
      onSelectBrowseMode()
    }
    if (activeId && (e.key === "Enter")) {
      const getRelevantId = (): string | undefined => {
        if (equals(activeList, favouritesList)) {
          return favouritesList.find(favouriteItem => favouriteItem.id === activeId)?.refId
        }
        return activeId
      }
      const relevantId = getRelevantId()
      if (relevantId !== undefined) {
        handleAddItemToDestination(relevantId)
      }
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      if (activeList.length === 0) {
        setActiveList(getActiveList)
      }
      handleArrowNavigation(
        "ArrowDown",
        latestActiveList.current,
        addedItemIds,
      )
    }
    if (e.key === "ArrowUp") {
      e.preventDefault()
      if (activeList.length === 0) {
        setActiveList(getActiveList)
      }
      handleArrowNavigation(
        "ArrowUp",
        latestActiveList.current,
        addedItemIds,
      )
    }
    const allowSideArrows = !e.shiftKey && inputValue.length === 0
    if (e.key === "ArrowRight" && allowSideArrows) {
      e.preventDefault()
      if (!isSearchResultVisible && recentList.length !== 0 && !equals(recentList, activeList)) {
        setActiveList(recentList)
        handleArrowNavigation(
          "ArrowRightOrLeft",
          latestActiveList.current,
          addedItemIds,
        )
      }
    }
    if (e.key === "ArrowLeft" && allowSideArrows) {
      e.preventDefault()
      if (!isSearchResultVisible && favouritesList.length !== 0 && !equals(favouritesList, activeList)) {
        setActiveList(favouritesList)
        handleArrowNavigation(
          "ArrowRightOrLeft",
          latestActiveList.current,
          addedItemIds,
        )
      }
    }
  }, [
    activeIdIndex,
    activeId,
    activeList,
    recentList,
    favouritesList,
    isSearchResultVisible,
  ])

  useEvent("keydown", onKeyDown)

  const onSelectBrowseMode = () => {
    onChangeViewMode(ViewMode.BROWSE)
    setIsSearchResultVisible(false)
    // Using a blank string as search term will list all items
    onSearchItems(SEARCH_ALL_ITEMS)
  }

  const onSelectSearchMode = () => {
    onChangeViewMode(ViewMode.SEARCH)
    inputRef.current?.focus()
    if (inputValue.trim().length > 0) {
      onSearchItems(inputValue)
      setIsSearchResultVisible(true)
    }
  }

  const renderResultList = (): React.ReactElement<unknown> => {
    if (isSearchResultVisible) {
      return (
        <SearchResults>
          <Column>
            <SearchDropdownList
              header={(
                <ListHeaderPrimary>
                  {(
                    isSearching ? (<Trans id="label_searching" message="Searching..."/>)
                      : (
                        <>
                          <Trans id="label_results" message="Results"/>{`(${searchResultList.length})`}
                          {searchSortDropdown}
                        </>
                      )
                  )}
                </ListHeaderPrimary>
              )}
              list={searchResultList}
              addedItemIds={addedItemIds}
              favouritesList={favouritesList}
              activeId={activeId}
              onMouseMove={onMouseMove}
              onAddItemToDestination={handleAddItemToDestination}
              onFavouriteItem={onFavouriteItem}
              onUnfavouriteItem={onUnfavouriteItem}
              searchText={inputValue}
              showFavouriteButton={showFavouriteButton}
            />
          </Column>
        </SearchResults>
      )
    }
    if (isBrowseModeEnabled) {
      return (
        <SearchResults>
          <Column>
            <SearchDropdownList
              header={(
                <ListHeader>
                  <Trans id="label_browse" message="Browse"/>
                </ListHeader>
              )}
              list={searchResultList}
              addedItemIds={addedItemIds}
              favouritesList={favouritesList}
              activeId={activeId}
              onMouseMove={onMouseMove}
              onAddItemToDestination={handleAddItemToDestination}
              onFavouriteItem={onFavouriteItem}
              onUnfavouriteItem={onUnfavouriteItem}
              searchText={inputValue}
              showFavouriteButton={showFavouriteButton}
            />
          </Column>
        </SearchResults>
      )
    }
    return (
      <ListsWrapper>
        <Column>
          <SearchDropdownDraggableList
            header={(
              <ListHeader>
                <StyledFavouritedIcon name="favourited"/>
                <Trans id="label_favourites" message="Favourites"/>
              </ListHeader>
            )}
            list={favouritesList}
            addedItemIds={addedItemIds}
            activeId={activeId}
            onMouseMove={onMouseMove}
            onAddItemToDestination={handleAddItemToDestination}
            onUnfavouriteItem={onUnfavouriteItem}
            onReorderDraggableItem={onReorderDraggableItem}
          />
        </Column>
        <Column>
          <SearchDropdownList
            header={(
              <RecentListHeader>
                <Trans id="label_recent" message="Recent"/>
                {recentSortDropdown}
              </RecentListHeader>
            )}
            list={recentList}
            addedItemIds={addedItemIds}
            favouritesList={favouritesList}
            activeId={activeId}
            onMouseMove={onMouseMove}
            onAddItemToDestination={handleAddItemToDestination}
            onFavouriteItem={onFavouriteItem}
            onUnfavouriteItem={onUnfavouriteItem}
            showFavouriteButton={showFavouriteButton}
          />
        </Column>
      </ListsWrapper>
    )
  }

  return (
    <DropdownContent ref={ref} style={style} className={DROPDOWN_CONTENT_CLASSNAME}>
      <InputWrapper>
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={({ target: { value, }}: React.ChangeEvent<HTMLInputElement>): void => setInputValue(value)}
        />
        {isSearching && !isBrowseModeEnabled ? (
          <StyledSpinner/>
        ) : (
          <div
            title={t({
              id: "button_search_mode_tooltip",
              message: "Switch to Search mode (as a shortcut you can also start typing text in the search field).{lineBreak}You can use a plus sign (+) in front of a term to make it mandatory. Use a minus sign (-) to exclude terms.",
              values: { lineBreak },
            })}
            onClick={onSelectSearchMode}
          >
            <StyledSearchThinIcon name="search-thin" isActive={!isBrowseModeEnabled}/>
          </div>
        )}
        <div
          title={t({ id: "button_browse_mode_tooltip", message: "Switch to Browse mode (as a shortcut you can also press the space bar while the search field is empty)." })}
          onClick={onSelectBrowseMode}
        >
          <BrowseModeIcon isActive={isBrowseModeEnabled}/>
        </div>
      </InputWrapper>
      {renderResultList()}
    </DropdownContent>
  )
})

export default SearchDropdownContent
